import { supabase } from '@/utils/supabase';
import type { PayoutSettlement } from '../../supabase/functions/_shared/payout-settlement';

export interface RecaptureProfitability {
  complete: boolean;
  missing_rows: number;
  earned: number | null;
  /** Absent on historical submission snapshots captured before bonuses. */
  bonuses?: number;
  /** Permanent elections less returned unused installments; absent before volume bonuses. */
  volume_bonus_committed?: number;
  absorbed: number;
  available: number | null;
}

export interface RecaptureSource extends PayoutSettlement {
  payout_id: string;
  loan_number: string;
  payout_type: string;
  banker_recon_id: string | null;
}

export interface RecaptureOrigin {
  payout_id: string;
  recipient_id: string;
  recipient_name: string;
  loan_number: string;
  gross_commission: number;
  borrower_name?: string | null;
  fund_date?: string | null;
  purchase_date?: string | null;
}

export interface RecaptureContext {
  origin: RecaptureOrigin & {
    banker_recon_id: string;
    net: number;
    cash: number;
    recaptured: number;
    loan_amount: number;
    purchase_price: number | null;
    premium: number | null;
    principal_collected: number;
    purchase_date: string | null;
    fund_date: string | null;
    epo_clear_date: string | null;
    eligible: boolean;
  };
  sources: RecaptureSource[];
  profitability: RecaptureProfitability;
  balance_token: string;
}

export interface RecaptureAllocationInput {
  kind: 'commission' | 'profitability';
  payout_id?: string;
  amount: number;
}

export interface RecaptureActivity {
  id: string;
  loan_number: string;
  reason: 'epo';
  status: 'draft' | 'submitted' | 'reversed' | 'cancelled';
  target_amount: number;
  submitted_at: string | null;
  reversed_at: string | null;
  reversal_reason: string | null;
  allocations: (RecaptureAllocationInput & {
    loan_number?: string | null;
    source_snapshot?: RecaptureSource | RecaptureProfitability;
  })[];
}

export interface RecaptureRecord extends RecaptureActivity {
  origin_payout_id: string | null;
  origin_payout_id_snapshot: string;
  recipient_id: string;
  recipient_name: string;
  claim_amount: number | null;
  invoice_reference: string;
  invoice_date: string | null;
  payoff_date: string | null;
  notes: string;
  draft_allocations: RecaptureAllocationInput[];
  version: number;
  replacement_of: string | null;
  origin_cash_warning: boolean;
  submission_snapshot: RecaptureContext | null;
  created_at: string;
  attachments: RecaptureAttachment[];
  events: { id: string; event_type: string; actor_name: string; created_at: string; details: Record<string, unknown> }[];
}

export interface RecaptureAttachment {
  id: string;
  storage_path: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  client_sha256: string;
  uploaded_at: string;
}

export type RecapturePaymentWarning = Pick<RecaptureRecord, 'recipient_id' | 'target_amount' | 'status'>;

export interface RecaptureWorkspace {
  enabled: boolean;
  records: RecaptureRecord[];
  origins: RecaptureOrigin[];
  context: RecaptureContext | null;
}

export interface RecaptureDraftInput {
  id?: string;
  version?: number;
  origin_payout_id: string;
  reason: 'epo';
  claim_amount: number | null;
  invoice_reference: string;
  invoice_date: string | null;
  payoff_date: string | null;
  notes: string;
  allocations: RecaptureAllocationInput[];
  replacement_of?: string;
}

export interface RecaptureAccount {
  profitability: RecaptureProfitability;
  records: RecaptureActivity[];
}

/** Shared compensation refresh, including profitability credits. */
export const RECAPTURE_CHANGED_EVENT = 'lendwise:recapture-changed';

function notifyChanged(): void {
  window.dispatchEvent(new Event(RECAPTURE_CHANGED_EVENT));
}

export async function getRecaptureWorkspace(originPayoutId: string | null = null): Promise<RecaptureWorkspace> {
  const { data, error } = await supabase.rpc('get_recapture_workspace', { p_origin_payout_id: originPayoutId });
  if (error) {
    throw error;
  }
  return data as RecaptureWorkspace;
}

/** Null means no visible recapture or bonus activity; skips the lifetime report. */
export async function getRecaptureAccount(recipientId: string): Promise<RecaptureAccount | null> {
  const { data, error } = await supabase.rpc('get_recapture_account_summary', { p_recipient_id: recipientId });
  if (error) {
    throw error;
  }
  return data as RecaptureAccount | null;
}

export async function getRecapturePaymentWarnings(): Promise<RecapturePaymentWarning[]> {
  const { data, error } = await supabase.rpc('get_recapture_payment_warnings');
  if (error) {
    throw error;
  }
  return data as RecapturePaymentWarning[];
}

async function sha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function downloadRecaptureBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface EvidenceUpload {
  uploadId: string;
  fileName: string;
  contentType: string;
  size: number;
  ticket?: RecaptureAttachment;
  uploaded: boolean;
  completed: boolean;
  pending?: Promise<void>;
}

// Keep only identifiers and progress, not document bytes. Retries must retain
// the metadata accepted by reserve_recapture_attachment for this exact digest.
const evidenceUploads = new Map<string, EvidenceUpload>();

function evidenceAlreadyUploaded(error: unknown): boolean {
  const failure = error as { code?: string; error?: string; status?: number; statusCode?: string; httpStatusCode?: number; message?: string };
  if (Number(failure.status ?? failure.statusCode ?? failure.httpStatusCode) !== 409) {
    return false;
  }
  return ['ResourceAlreadyExists', 'KeyAlreadyExists', 'already_exists'].includes(failure.code ?? failure.error ?? '')
    // The installed storage-js exposes only status/statusCode/message.
    || /^(?:The )?(?:resource|key) already exists\.?$/i.test(failure.message ?? '');
}

async function finishEvidenceUpload(id: string, file: File, digest: string, attempt: EvidenceUpload): Promise<void> {
  if (!attempt.ticket) {
    const { data, error } = await supabase.rpc('reserve_recapture_attachment', {
      p_id: id, p_upload_id: attempt.uploadId, p_file_name: attempt.fileName,
      p_content_type: attempt.contentType, p_size_bytes: attempt.size, p_sha256: digest
    });
    if (error) {
      throw error;
    }
    attempt.ticket = data as RecaptureAttachment;
    attempt.uploaded = Boolean(attempt.ticket.uploaded_at);
  }
  if (!attempt.uploaded) {
    const uploaded = await supabase.storage.from('recapture-evidence').upload(attempt.ticket.storage_path, file, {
      contentType: attempt.contentType, upsert: false
    });
    if (uploaded.error && !evidenceAlreadyUploaded(uploaded.error)) {
      throw uploaded.error;
    }
    attempt.uploaded = true;
  }
  // Completion verifies stored object metadata. Already-completed tickets can
  // replay even after submission or after reaching the completed-file limit.
  const completed = await supabase.rpc('complete_recapture_attachment', { p_upload_id: attempt.uploadId });
  if (completed.error) {
    throw completed.error;
  }
  attempt.completed = true;
  notifyChanged();
}

export async function uploadRecaptureEvidence(id: string, file: File): Promise<void> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!['pdf', 'xlsx'].includes(extension || '') || file.size < 1 || file.size > 20 * 1024 * 1024) {
    throw new Error('Choose a PDF or XLSX file of up to 20 MB.');
  }
  const contentType = extension === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const digest = await sha256(file);
  const key = `${id}:${digest}`;
  let attempt = evidenceUploads.get(key);
  if (!attempt) {
    attempt = { uploadId: crypto.randomUUID(), fileName: file.name, contentType, size: file.size, uploaded: false, completed: false };
    evidenceUploads.set(key, attempt);
  }
  if (attempt.fileName !== file.name || attempt.contentType !== contentType || attempt.size !== file.size) {
    throw new Error('Retry this evidence with the same file name and type as the original upload.');
  }
  if (attempt.completed) {
    return;
  }
  const current = attempt;
  current.pending ??= finishEvidenceUpload(id, file, digest, current).finally(() => {
    current.pending = undefined;
  });
  await current.pending;
}

export async function downloadRecaptureEvidence(file: RecaptureAttachment): Promise<void> {
  const { data, error } = await supabase.storage.from('recapture-evidence').download(file.storage_path);
  if (error) {
    throw error;
  }
  if (await sha256(data) !== file.client_sha256) {
    throw new Error('The downloaded file does not match its recorded digest. Ask Accounting to review the evidence.');
  }
  downloadRecaptureBlob(data, file.file_name);
}

export async function saveRecaptureDraft(input: RecaptureDraftInput): Promise<string> {
  const { data, error } = await supabase.rpc('save_recapture_draft', { p_input: input });
  if (error) {
    throw error;
  }
  notifyChanged();
  return data as string;
}

export async function submitRecapture(id: string, version: number, balanceToken: string, submitKey: string): Promise<void> {
  const { error } = await supabase.rpc('submit_recapture', {
    p_id: id, p_version: version, p_balance_token: balanceToken, p_submit_key: submitKey
  });
  if (error) {
    throw error;
  }
  notifyChanged();
}

export async function reverseRecapture(id: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('reverse_recapture', { p_id: id, p_reason: reason });
  if (error) {
    throw error;
  }
  notifyChanged();
}

export async function cancelRecaptureDraft(id: string, version: number): Promise<void> {
  const { error } = await supabase.rpc('cancel_recapture_draft', { p_id: id, p_version: version });
  if (error) {
    throw error;
  }
  notifyChanged();
}
